'use client'

// src/file-upload-context.ts
import { createContext } from "@chakra-ui/utils";
import { normalizeProps, useMachine } from "@zag-js/react";
import * as fileUpload from "@zag-js/file-upload";
import { useId } from "react";
var [FileUploadStylesProvider, useFileUploadStyles] = createContext({
  name: "FileUploadStylesProvider"
});
var [FileUploadProvider, useFileUploadContext] = createContext({
  name: "FileUploadProvider"
});
var useFileUpload = (props) => {
  const { isDisabled, ...rest } = props;
  const initialContext = {
    id: useId(),
    disabled: isDisabled,
    ...rest
  };
  const [state, send] = useMachine(fileUpload.machine(initialContext), {
    context: {
      ...initialContext
    }
  });
  return fileUpload.connect(state, send, normalizeProps);
};

// src/file-upload-theme.ts
import { createMultiStyleConfigHelpers } from "@chakra-ui/styled-system";
var parts = ["container", "dropzone", "button"];
var { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts);
var baseStyle = definePartsStyle({
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    minHeight: 1,
    borderRadius: "md",
    borderWidth: 1,
    borderStyle: "dashed",
    py: 4,
    px: 6,
    transitionProperty: "common",
    transitionDuration: "normal",
    "&[data-dragging]:not([data-disabled])": {
      bg: "blackAlpha.100",
      _dark: {
        bg: "whiteAlpha.100"
      }
    }
  }
});
var fileUploadTheme = defineMultiStyleConfig({
  defaultProps: {},
  baseStyle
});

// src/file-upload.tsx
import * as React from "react";
import {
  chakra,
  forwardRef,
  useMultiStyleConfig,
  Image,
  useEnvironment
} from "@chakra-ui/react";
import { pick, runIfFn, split } from "@chakra-ui/utils";
import { jsx, jsxs } from "react/jsx-runtime";
var FileUpload = forwardRef((props, ref) => {
  const {
    children,
    inputRef,
    colorScheme,
    size,
    variant,
    styleConfig,
    onFilesChange,
    ...rest
  } = props;
  const [options, containerProps] = split(rest, [
    "translations",
    "accept",
    "allowDrop",
    "dir",
    "isDisabled",
    "validate",
    "locale",
    "maxFileSize",
    "maxFiles",
    "minFileSize",
    "name",
    "onFileAccept",
    "onFileChange",
    "onFileReject",
    "getRootNode"
  ]);
  const env = useEnvironment();
  const context = useFileUpload({
    getRootNode: env.getDocument,
    onFileChange: onFilesChange,
    ...options
  });
  const styles = useMultiStyleConfig("SuiFileUpload", {
    styleConfig: styleConfig != null ? styleConfig : fileUploadTheme,
    size,
    variant,
    colorScheme
  });
  const renderContext = {
    ...pick(context, [
      "acceptedFiles",
      "rejectedFiles",
      "clearFiles",
      "createFileUrl",
      "deleteFile",
      "getFileSize",
      "dragging",
      "focused",
      "openFilePicker",
      "setFiles"
    ]),
    open: context.openFilePicker,
    files: context.acceptedFiles
  };
  return /* @__PURE__ */ jsx(FileUploadStylesProvider, { value: styles, children: /* @__PURE__ */ jsx(FileUploadProvider, { value: context, children: /* @__PURE__ */ jsxs(
    chakra.div,
    {
      ref,
      ...containerProps,
      ...context.getRootProps(),
      __css: styles.container,
      children: [
        /* @__PURE__ */ jsx("input", { ref: inputRef, ...context.getHiddenInputProps() }),
        runIfFn(children, renderContext)
      ]
    }
  ) }) });
});
var FileUploadDropzone = forwardRef((props, ref) => {
  const { children, ...rest } = props;
  const { getDropzoneProps } = useFileUploadContext();
  const styles = useFileUploadStyles();
  const { onClick, ...dropzoneProps } = getDropzoneProps();
  return /* @__PURE__ */ jsx(chakra.div, { ref, ...rest, ...dropzoneProps, __css: styles.dropzone, children });
});
var FileUploadTrigger = forwardRef(
  (props, ref) => {
    const { children, ...rest } = props;
    const { getTriggerProps } = useFileUploadContext();
    const styles = useFileUploadStyles();
    return /* @__PURE__ */ jsx(
      chakra.button,
      {
        ref,
        ...rest,
        ...getTriggerProps(),
        __css: styles.button,
        children
      }
    );
  }
);
var FileUploadPreview = forwardRef(
  (props, ref) => {
    const { file, ...rest } = props;
    const [src, setSrc] = React.useState();
    const cleanup = React.useRef(null);
    const { createFileUrl } = useFileUploadContext();
    React.useEffect(() => {
      if (file) {
        cleanup.current = createFileUrl(file, (url) => setSrc(url));
      }
      return () => {
        if (cleanup.current) {
          cleanup.current();
          cleanup.current = null;
        }
      };
    }, [file]);
    return /* @__PURE__ */ jsx(Image, { ref, ...rest, src });
  }
);
export {
  FileUpload,
  FileUploadDropzone,
  FileUploadPreview,
  FileUploadProvider,
  FileUploadStylesProvider,
  FileUploadTrigger,
  fileUploadTheme,
  useFileUpload,
  useFileUploadContext,
  useFileUploadStyles
};
//# sourceMappingURL=index.mjs.map